// ============================================================================
// Booking pace — "on the books" (OTB) site-nights for future weeks, compared
// with the same weeks last year as they looked at the same point in time.
//
// APPROXIMATION: OTB-as-of-date is reconstructed from created_at on
// current-version reservations. A stay modified after the as-of date carries
// its original created_at but its *current* shape, so history isn't perfectly
// reproduced; accurate pacing needs backend snapshots (design doc §6.8).
// ============================================================================

import { addDays } from '../dateUtils';
import { bucketLabel } from './aggregations';
import { AnalysisStay, isRevenueStay } from './types';

export interface PacePoint {
  weekStart: string; // this year's week
  label: string;
  thisYear: number; // OTB site-nights as of today
  lastYear: number; // OTB site-nights for the same week last year, as of a year ago
}

function activeSiteCount(stay: AnalysisStay): number {
  return Math.max(1, stay.sites.filter(s => s.status === 'active').length);
}

/** OTB site-nights for the week [weekStart, weekStart+7) among stays created on or before asOf. */
export function otbSiteNightsForWeek(stays: AnalysisStay[], weekStart: string, asOf: string): number {
  const weekEnd = addDays(weekStart, 7); // exclusive
  let nights = 0;
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    if (!stay.createdAt || stay.createdAt.split('T')[0] > asOf) continue;
    if (stay.checkOut <= weekStart || stay.checkIn >= weekEnd) continue;
    const start = stay.checkIn > weekStart ? stay.checkIn : weekStart;
    const end = stay.checkOut < weekEnd ? stay.checkOut : weekEnd;
    let count = 0;
    for (let night = start; night < end; night = addDays(night, 1)) count += 1;
    nights += count * activeSiteCount(stay);
  }
  return nights;
}

/**
 * Pace curve for the next `horizonWeeks` weeks starting at the week of
 * `today`, vs the same weeks last year (shifted −364 days to keep weekday
 * alignment) as they looked as of `today − 364`.
 */
export function paceCurve(stays: AnalysisStay[], today: string, horizonWeeks: number): PacePoint[] {
  const lastYearAsOf = addDays(today, -364);
  const points: PacePoint[] = [];
  for (let week = 0; week < horizonWeeks; week++) {
    const weekStart = addDays(today, week * 7);
    const lastYearWeekStart = addDays(weekStart, -364);
    points.push({
      weekStart,
      label: bucketLabel(weekStart, 'week'),
      thisYear: otbSiteNightsForWeek(stays, weekStart, today),
      lastYear: otbSiteNightsForWeek(stays, lastYearWeekStart, lastYearAsOf),
    });
  }
  return points;
}
